import { LoadingStreamLoadingInfo } from '@/actions/shared/MultipartStreamParser';
import { EnsuranceOffer } from '@/actions/types/EnsuranceOffer';
import { GetIsDevMode } from '@/forms/shared/IsDevMode';
import { useAppSelector } from '@/hooks';
import { FormularEvaluateBoolean, GetEvaluationContext } from '@/lib/FormularEvaluate';
import { useMemoStateFromFilterDeps } from '@/lib/useMemoStateFromFilterDeps';
import { useEffect, useMemo } from 'react';

const defaultValue = {
  list: null as EnsuranceOffer[] | null,
  requestedEnsurances: null as string[] | null,
  presetHeadpoints: {},
  submitting: false,
  loading: false,
  loadingSelectedInsurance: false,
  predefLoading: false,
  loadingInfo: {
    total: undefined as string,
    current: undefined as string,
  },
};

export default function ensuranceList(state = defaultValue, action): typeof defaultValue {
  if (action.type === 'ENSURANCES_LOADED') {
    return {
      ...state,
      list: action.list,
      requestedEnsurances: action.requestedEnsurances,
      loading: false,
      loadingInfo: defaultValue.loadingInfo,
    };
  } else if (action.type === 'ENSURANCES_LOADING_INFO') {
    const payload: LoadingStreamLoadingInfo = action.payload;

    const existingNames = new Set(state.list?.filter((o) => o).map((o) => o.name) || []);
    const newItems = payload.offersLoaded?.filter((o) => !existingNames.has(o.name));

    return {
      ...state,
      ...(!payload.offersLoaded ? null : { list: [...newItems, ...(state.list || [])] }),
      loadingInfo: {
        total: payload.totalItems,
        current: payload.currentItem,
      },
    };
  } else if (action.type === 'ENSURANCES_LOAD_STARTED') {
    return { ...state, loading: true, loadingInfo: defaultValue.loadingInfo, list: null };
  } else if (action.type === 'ENSURANCES_LOAD_END') {
    return { ...state, loading: false, loadingInfo: defaultValue.loadingInfo };
  } else if (action.type === 'ENSURANCES_RESET') {
    return { ...state, loading: false, loadingInfo: null, list: null };
  } else if (action.type === 'ENSURANCE_SELECTED') {
    return { ...state, loadingSelectedInsurance: true };
  } else if (action.type === 'SUBMIT_LOADING') {
    return { ...state, loading: true, submitting: true };
  } else if (action.type === 'SUBMIT_COMPLETED') {
    return { ...state, loading: false, submitting: false };
  } else if (action.type === 'ENSURANCE_SELECTED_COMPLETED') {
    return { ...state, loadingSelectedInsurance: false };
  } else if (action.type === 'PREDEF_LOADING') {
    return { ...state, predefLoading: true };
  } else if (action.type === 'PREDEF_COMPLETED') {
    return { ...state, predefLoading: false };
  } else if (action.type === 'PRESET_LOADED') {
    return { ...state, presetHeadpoints: action.preset.headpoints || {} };
  }

  return state;
}

export const useFilteredEnsuranceList = () => {
  const list = useAppSelector((state) => state.ensuranceList?.list);
  const filter = useAppSelector((state) => state.ensfields.clientSideGlobalFilter);

  // Wenn sich irgendein Feld ändert, welches in globalen filtern verwendet wird,
  // dann wird der state neu abgerufen.
  const mergedFilter = useMemo(() => {
    return filter.map((f) => f.formular).join(' || ');
  }, []);

  const memoState = useMemoStateFromFilterDeps(mergedFilter);

  return useMemo(() => {
    const devmode = GetIsDevMode();

    const filteredList =
      list?.map((ensItem) => {
        if (ensItem.failed) return ensItem;

        const context = GetEvaluationContext(memoState, null, ensItem.name);

        for (const f of filter) {
          const filterRes = FormularEvaluateBoolean(f.formular, context);

          if (devmode) {
            console.log(`State for filter changed ens-${ensItem.name} filter-${f.displayName}`, {
              memoState,
              filter: f,
              context,
              filterRes,
              ensItem,
              taxonomie: memoState.ensfields.ensuranceTaxonomie[ensItem.name],
            });
          }

          if (!filterRes) {
            return {
              ...ensItem,
              failed: true,
              type: GetIsDevMode() || Boolean(f.displayAnyway) ? 'Generisch' : 'Ausgeblendet',
              reason: f.errorText,
            };
          }
        }
        return ensItem;
      }) || [];
    return filteredList;
  }, [memoState, filter, list]);
};
